// HeroForge details (docs/59): the sheet a card's "i" button opens, saying what a background,
// loadout, species, feat, specialty, patron, companion or spell does with the catalogue's numbers
// (`detailsDoc` in ../forge.ts builds the words; this file only lays them out).
//
// The sheet is a library Modal. HeroForge itself sits inside a Modal in the World screen, and a
// Modal's Escape/Tab handling is a document capture listener, so the outer one would also hear
// them. `useTopmostKeys` listens on window in the capture phase (ahead of document) while the
// sheet is open: Escape closes only the sheet, Tab cycles only through it, and neither reaches
// the outer dialog. Focus returns to the button that opened it (Modal restores it on close).
import { useEffect, useState, type MouseEvent } from 'react';
import { Badge, HeroSprite, IconButton, Modal, Stat, Stats } from '../../components';
import { ItemIcon } from '../../inventory/ItemCard';
import { type AbilityDoc, type DetailsDoc, type DetailsKind } from '../forge';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function useTopmostKeys(open: boolean, testId: string, onClose: () => void): void {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' && e.key !== 'Tab') return;
      const el = document.querySelector<HTMLElement>(`[data-testid="${testId}"]`);
      if (!el) return;
      e.stopPropagation();
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      const items = [...el.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((n) => n.offsetParent !== null);
      e.preventDefault();
      if (!items.length) { el.focus(); return; }
      const i = items.indexOf(document.activeElement as HTMLElement);
      const next = e.shiftKey ? (i <= 0 ? items.length - 1 : i - 1) : (i === -1 || i === items.length - 1 ? 0 : i + 1);
      items[next]!.focus();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, testId, onClose]);
}

/** The small "i" inside a choice card; never selects the card it sits on. */
export function DetailsButton({ kind, id, name, onOpen }: { kind: DetailsKind; id: number; name: string; onOpen: (kind: DetailsKind, id: number) => void }) {
  const open = (e: MouseEvent) => { e.stopPropagation(); e.preventDefault(); onOpen(kind, id); };
  return (
    <IconButton label={`Details: ${name}`} variant="ghost" small className="fk-forge__details-btn" data-sfx="ui.tab"
      data-testid="forge-details-open" data-details-kind={kind} data-details-id={id} onClick={open}>i</IconButton>
  );
}

export function AbilityCard({ a }: { a: AbilityDoc }) {
  return (
    <li className="fk-forge__dability">
      <div className="fk-forge__dability-head">
        <span className="fk-forge__dability-name">{a.name}</span>
        <Badge tone={a.slot === 'Bonus action' ? 'plum' : a.slot === 'Always on' ? 'grey' : 'blue'}>{a.slot}</Badge>
      </div>
      {a.slot !== 'Always on' && (
        <dl className="fk-forge__dterms">
          {a.range && <><dt>Range</dt><dd>{a.range}</dd></>}
          <dt>Cost</dt><dd>{a.cost}</dd>
          <dt>Cooldown</dt><dd>{a.cooldown}</dd>
        </dl>
      )}
      <p className="fk-forge__dability-text">{a.text}</p>
    </li>
  );
}

export function DetailsSheet({ doc: live, onClose }: { doc: DetailsDoc | undefined; onClose: () => void }) {
  const open = !!live;
  // keep the last sheet's words on screen through the Modal's exit animation
  const [last, setLast] = useState<DetailsDoc | undefined>(live);
  useEffect(() => { if (live) setLast(live); }, [live]);
  const doc = live ?? last;
  useTopmostKeys(open, 'forge-details', onClose);
  return (
    <Modal open={open} onClose={onClose} eyebrow={doc?.eyebrow} title={doc ? (doc.subtitle ? `${doc.title}, ${doc.subtitle}` : doc.title) : undefined}
      size="narrow" testId="forge-details" className="fk-forge__details">
      {doc && (
        <div className="fk-forge__dbody">
          {(doc.frame !== undefined || doc.blurb) && (
            <div className="fk-forge__dlead">
              {doc.frame !== undefined && <span className="fk-forge__dportrait"><HeroSprite frame={doc.frame} scale={3} title={doc.title} /></span>}
              {doc.blurb && <p className="fk-forge__dblurb">{doc.blurb}</p>}
            </div>
          )}
          {doc.stats.length > 0 && (
            <Stats className="fk-forge__dstats" aria-label="Numbers">
              {doc.stats.map((s) => <Stat key={s.label} label={s.label} value={s.value} />)}
            </Stats>
          )}
          {doc.paras.map((p, i) => <p key={i} className="fk-forge__dpara">{p}</p>)}
          {doc.items.length > 0 && (
            <ul className="fk-forge__ditems" aria-label="Items">
              {doc.items.map((it, i) => (
                <li key={i} className="fk-forge__ditem">
                  <ItemIcon def={it.def} tier={it.tier} name={it.name} scale={2} framed title={it.name} />
                  <div className="fk-forge__ditem-body">
                    <div className="fk-forge__ditem-head"><span className="fk-forge__ditem-name">{it.name}</span><span className="fk-forge__ditem-slot">{it.slot}</span></div>
                    <div className="fk-forge__ditem-family">{it.family}{it.price ? ` · worth ${it.price} gold` : ''}</div>
                    {it.lines.length > 0 && <div className="fk-forge__ditem-lines">{it.lines.map((l) => <span key={l}>{l}</span>)}</div>}
                    {it.lines.length === 0 && !it.use && <div className="fk-forge__ditem-lines fk-forge__ditem-lines--none"><span>no stat lines of its own</span></div>}
                    {it.use && <div className="fk-forge__ditem-use">{it.use}</div>}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {doc.abilities.length > 0 && <ul className="fk-forge__dabilities" aria-label="Abilities">{doc.abilities.map((a) => <AbilityCard key={`${a.id}-${a.name}`} a={a} />)}</ul>}
          {doc.notes.length > 0 && <ul className="fk-forge__dnotes">{doc.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>}
        </div>
      )}
    </Modal>
  );
}
